import React,  { useState } from "react";
import { Box, IconButton, Textarea, UpArrow } from "@commonsku/design-system";

export interface ChatInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onSubmit?: () => void;
  placeholder?: string;
  disabled?: boolean;
  minH?: string;
  borderColor?: string;
}

export function ChatInput(props: ChatInputProps) {
  const { value, onChange, onSubmit, placeholder = "Ask a question...", disabled = false, minH = "36px", borderColor = "gray.100" } = props;
  const hasText = (value ?? "").trim().length > 0;
  const canSubmit = hasText && !disabled;

  const [isFocused, setIsFocused] = useState(false);

  function handleFocus() {
    setIsFocused(true);
  }

  function handleBlur() {
    setIsFocused(false);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    const isComposing = event.nativeEvent.isComposing;
    if (event.key === "Enter" && !event.shiftKey && !isComposing) {
      event.preventDefault();
      submit();
    }
  }

  function submit() {
    if (!canSubmit) return;
    onSubmit?.();
  }

  return (
    <Box
      w="full"
      maxW="680px"
      minH="56px"
      display="flex"
      alignItems="flex-end"
      gap="8px"
      bg="#fff"
      padding="10px 10px 10px 16px"
      borderStyle="solid"
      borderWidth="1px"
      borderRadius="8px"
      borderColor={isFocused ? "brand.focusRing" : borderColor} // TODO: replace gray.200 with brand.subtle
      boxShadow={isFocused ? "0 0 0 2px var(--chakra-colors-brand-subtle)" : "none"}
      outline="none"
      transition="border-color 120ms ease, box-shadow 120ms ease"
      _hover={{ borderColor: "brand.focusRing" }}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      <Textarea
        value={value}
        onChange={(event) => onChange?.(event.target.value)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        placeholder={placeholder}
        readOnly={disabled}
        // @ts-ignore TODO: fix this (import @ark-ui/react)
        autoresize
        rows={1}
        flex="1"
        padding="0"
        resize="none"
        overflow="hidden"
        minH={minH}
        maxH="180px"
        fontSize="sm"
        lineHeight="1.45"
        margin="0"
        border="none"
        outline="none"
        boxShadow="none"
        paddingTop="8px"
        paddingBottom="8px"
        _placeholder={{ color: "text.subtle" }}
        _hover={{
          border: "none",
          outline: "none",
          boxShadow: "none",
        }}
        _focus={{
          border: "none",
          outline: "none",
          boxShadow: "none",
        }}
        _focusVisible={{
          border: "none",
          outline: "none",
          boxShadow: "none",
        }}
      />

      {hasText ? (
        <IconButton
          onClick={(e) => { e.stopPropagation(); submit(); }}
          label="Send message"
          type="submit"
          disabled={disabled}
          _hover={{ bg: "brand.fg" }}
          _disabled={{ bg: "border.muted" }}
          justifyContent="center"
          alignItems="center"
          borderRadius="4px"
          backgroundColor={disabled ? "border.subtle" : "brand.fg"}
          border="none"
          display="inline-flex"
          cursor={disabled ? "not-allowed" : "pointer"}
          transition="background 120ms ease, opacity 120ms ease"
          flexShrink={0}
          alignSelf="flex-end"
          size="sm"
        >
          <UpArrow size="lg" strokeWidth={2.5} />
        </IconButton>
      ) : null}
    </Box>
  );
}
